import Papa from 'papaparse';
import { MagnifyingGlass, Trash } from '@phosphor-icons/react';
import { useDebounce } from 'ahooks';
import { useEffect, useState } from 'react';
import { Button } from 'src/components/Button';
import { ContentWrapper } from 'src/components/ContentWrapper';
import { Input } from 'src/components/Input';
import { Label } from 'src/components/Label';
import { Modal } from 'src/components/Modal';
import { SelectBox } from 'src/components/SelectBox';
import { SideDropActions } from 'src/components/SideDropActions';
import Table, { Row } from 'src/components/Table';
import { Tooltip } from 'src/components/Tooltip';
import { useNotification } from 'src/contexts/NotificationContext';
import useAuthedRequest from 'src/hooks/useAuthedRequest';
import { DncNumber, DncType } from 'src/interfaces/dnc.interface';
import { QueryObject } from 'src/interfaces/queryObject.interface';
import {
  processPhoneNumber,
  formatPhoneNumberWithError,
} from 'src/utils/number';
import { Can, Subject } from 'src/hooks/usePermissions';
import { friendlyDateFormatter } from 'src/utils/date';

const DNC_TYPE_OPTIONS = {
  [DncType.USER]: { label: 'User', value: DncType.USER },
  [DncType.GLOBAL]: { label: 'Fluents', value: DncType.GLOBAL },
  [DncType.ALL]: { label: 'All', value: DncType.ALL },
};

const headers = [
  {
    key: 'phoneNumber',
    label: 'Phone Number',
    width: '20%',
    disableSorting: true,
  },
  { key: 'type', label: 'Created By', width: '15%', disableSorting: true },
  { key: 'createdAt', label: 'Date Added', width: '20%', disableSorting: true },
  { key: 'reason', label: 'Reason', width: '35%', disableSorting: true },
  { key: 'action', label: '', width: '10%', disableSorting: true },
];

export const ListDncNumbers = () => {
  const { get, post, remove } = useAuthedRequest();
  const notification = useNotification();

  const [rows, setRows] = useState<DncNumber[]>([]);
  const [filter, setFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState<DncType>(DncType.USER);
  const debouncedFilter = useDebounce(filter, { wait: 500 });
  const [loading, setLoading] = useState(false);
  const [loadingCsv, setLoadingCsv] = useState(false);
  const [size, setSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [modalDeleteOpen, setModalDeleteOpen] = useState(false);
  const [modalAddOpen, setModalAddOpen] = useState(false);
  const [selectedDncNumber, setSelectedDncNumber] = useState<DncNumber | null>(
    null,
  );
  const [addForm, setAddForm] = useState({
    phoneNumber: '',
    reason: '',
  });
  const [sortConfig, setSortConfig] = useState<{
    key: string | null;
    direction: string | null;
  }>({ key: null, direction: null });

  const fetchData = async (page: number, perPage: number) => {
    setLoading(true);

    const queryObject: QueryObject = {
      page: String(page),
      size: String(perPage),
      filters: JSON.stringify({
        phoneNumber: filter ? filter : undefined,
      }),
    };

    if (typeFilter !== DncType.ALL) {
      queryObject.type = typeFilter;
    }

    if (sortConfig.key) {
      queryObject.sort_column = sortConfig.key;
      queryObject.sort_desc =
        sortConfig.direction === 'desc' ? 'true' : 'false';
    }

    const query = new URLSearchParams(queryObject as Record<string, string>);

    const data = await get(`/dnc-numbers?${query.toString()}`);

    const items = data.items.map((item: DncNumber) => ({
      ...item,
      phoneNumber: processPhoneNumber(item.phoneNumber),
    }));

    setRows(items);
    setTotalItems(data.total);
    setLoading(false);
  };

  useEffect(() => {
    if (!loading) {
      fetchData(currentPage, size);
    }
  }, [currentPage, sortConfig, debouncedFilter, size, typeFilter]);

  const handlePageChange = (page: number, perPage: number) => {
    setCurrentPage(page);
    setSize(perPage);
  };

  const handleSizeChange = (perPage: number) => {
    setSize(perPage);
    setCurrentPage(1);
    fetchData(1, perPage);
  };

  const handleSort = (key: string, direction: string) => {
    setSortConfig({ key, direction });
  };

  const submitFilter = () => {
    handlePageChange(1, size);
  };

  const openDeleteModal = (dncNumber: DncNumber) => {
    setSelectedDncNumber(dncNumber);
    setModalDeleteOpen(true);
  };

  const confirmDeleteAction = async () => {
    if (selectedDncNumber) {
      setLoading(true);

      await remove(`/dnc-numbers/${selectedDncNumber.id}`);

      notification.success('DNC number removed successfully');
      setModalDeleteOpen(false);
      fetchData(currentPage, size);
      setSelectedDncNumber(null);
      setLoading(false);
    }
  };

  const closeDeleteModal = () => {
    setModalDeleteOpen(false);
    setSelectedDncNumber(null);
  };

  const openAddModal = () => {
    setModalAddOpen(true);
  };

  const closeAddModal = () => {
    setModalAddOpen(false);
    setAddForm({ phoneNumber: '', reason: '' });
  };

  const confirmAddAction = async () => {
    if (!addForm.phoneNumber.trim()) {
      notification.error('Phone number is required');
      return;
    }

    // Validate phone number format
    const { number, error } = formatPhoneNumberWithError(addForm.phoneNumber);
    if (error) {
      notification.error(error);
      return;
    }

    setLoading(true);

    await post('/dnc-numbers', {
      phoneNumber: number,
      reason: addForm.reason.trim() || 'Manually added through dashboard',
    });

    notification.success('DNC number added successfully');
    closeAddModal();
    fetchData(currentPage, size);
    setLoading(false);
  };

  const getReasonDisplay = (dncNumber: DncNumber) => {
    if (dncNumber.reason) {
      return dncNumber.reason;
    }

    return 'No reason provided';
  };

  const getTypeDisplay = (type: DncType) => {
    return type === DncType.GLOBAL ? 'Fluents' : 'User';
  };

  const exportReportToCSV = async () => {
    try {
      setLoadingCsv(true);
      const queryObject: QueryObject = {
        filters: JSON.stringify({
          phoneNumber: filter ? filter : undefined,
        }),
      };

      if (typeFilter !== DncType.ALL) {
        queryObject.type = typeFilter;
      }

      if (sortConfig.key) {
        queryObject.sort_column = sortConfig.key;
        queryObject.sort_desc =
          sortConfig.direction === 'desc' ? 'true' : 'false';
      }

      const query = new URLSearchParams(queryObject as Record<string, string>);

      const response = await get(`/dnc-numbers/report?${query.toString()}`);
      const formattedCallRecordsWithHeaders = response.map((row: DncNumber) =>
        Object({
          createdAt: row.createdAt,
          updatedAt: row.updatedAt,
          phoneNumber: row.phoneNumber,
          reason: row.reason,
        }),
      );

      const blob = new Blob([Papa.unparse(formattedCallRecordsWithHeaders)], {
        type: 'text/csv;charset=utf-8;',
      });
      const url = URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.setAttribute('href', url);
      link.setAttribute('download', 'calls.csv');
      link.click();

      URL.revokeObjectURL(url);
      return notification.success('CSV was downloaded successfully');
    } catch (error) {
      console.error('Error generating CSV:', error);
      notification.error('Failed to generate CSV');
    } finally {
      setLoadingCsv(false);
    }
  };

  return (
    <>
      <ContentWrapper>
        <div className="flex flex-col md:flex-row justify-between mb-6">
          <div className="flex w-full md:w-1/2 self-end">
            <div className="flex w-full md:w-96 self-end">
              <Can operation="read" subject={Subject.DNC}>
                <Input
                  type="text"
                  required
                  placeholder="Search phone numbers"
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                  onKeyUp={(e) => {
                    if (e.key === 'Enter') {
                      submitFilter();
                    }
                  }}
                  icon={<MagnifyingGlass size={20} />}
                  iconPosition="left"
                  inputAction={() => fetchData(currentPage, size)}
                />
              </Can>
            </div>

            <div className="w-40 ml-2">
              <Label htmlFor="dnc-type">Created By</Label>
              <SelectBox
                key="dnc-type"
                options={Object.values(DNC_TYPE_OPTIONS)}
                defaultValue={
                  typeFilter
                    ? DNC_TYPE_OPTIONS[typeFilter]
                    : DNC_TYPE_OPTIONS[DncType.USER]
                }
                variant="outlined"
                color="primary"
                size="medium"
                onChange={(value) => setTypeFilter(value as DncType)}
                className="col-span-2"
              />
            </div>
          </div>

          <div className="mt-6 flex justify-end gap-2">
            <Button onClick={() => exportReportToCSV()} disabled={loadingCsv}>
              {loadingCsv ? 'Exporting CSV...' : 'Export CSV'}
            </Button>
            <Can operation="create" subject={Subject.DNC}>
              <Button onClick={openAddModal}>New</Button>
            </Can>
          </div>
        </div>

        <Can operation="read" subject={Subject.DNC}>
          <Table
            headers={headers}
            rows={rows.map(
              (row) =>
                ({
                  phoneNumber: row.phoneNumber,
                  type: (
                    <div className="flex items-center gap-2">
                      {getTypeDisplay(
                        row.userId ? DncType.USER : DncType.GLOBAL,
                      )}
                    </div>
                  ),
                  createdAt: friendlyDateFormatter(new Date(row.createdAt)),
                  reason: (
                    <Tooltip content={getReasonDisplay(row)} position="top">
                      <div className="max-w-xs truncate">
                        {getReasonDisplay(row)}
                      </div>
                    </Tooltip>
                  ),
                  action: (
                    <div
                      className="flex justify-center items-center"
                      onClick={(e) => {
                        e.stopPropagation();
                      }}
                    >
                      <SideDropActions
                        zIndex={60}
                        options={[
                          {
                            label: 'Remove from DNC',
                            Icon: Trash,
                            color: 'error',
                            show: !!row.userId,
                            onClick: () => openDeleteModal(row),
                          },
                        ]}
                      />
                    </div>
                  ),
                }) as Row,
            )}
            totalItems={totalItems}
            currentPage={currentPage}
            onPageChange={handlePageChange}
            onSort={handleSort}
            loading={loading}
            setSize={handleSizeChange}
            defaultSize={size}
            onRowClick={(index: number) => {
              const dncNumber = rows[index] as unknown as DncNumber;
              if (dncNumber && dncNumber.userId) {
                openDeleteModal(dncNumber);
              }
            }}
          />
        </Can>
      </ContentWrapper>

      {/* Add DNC Number Modal */}
      <Modal
        title="Add DNC Number"
        isOpen={modalAddOpen}
        onClose={closeAddModal}
        actionButton={
          <Button
            onClick={() => confirmAddAction()}
            disabled={loading || !addForm.phoneNumber.trim()}
          >
            Add DNC Number
          </Button>
        }
      >
        <div className="space-y-4">
          <div>
            <Label htmlFor="phone-number">Phone Number</Label>
            <Input
              type="text"
              id="phone-number"
              placeholder="Enter phone number"
              value={addForm.phoneNumber}
              onChange={(e) => {
                setAddForm({ ...addForm, phoneNumber: e.target.value });
              }}
              disabled={loading}
              required
            />
          </div>
          <div>
            <Label htmlFor="reason">Reason (Optional)</Label>
            <Input
              type="text"
              id="reason"
              placeholder="Enter reason for adding to DNC list"
              value={addForm.reason}
              onChange={(e) =>
                setAddForm({ ...addForm, reason: e.target.value })
              }
            />
          </div>
        </div>
      </Modal>

      {/* Remove DNC Number Modal */}
      <Modal
        title="Remove DNC Number"
        isOpen={modalDeleteOpen}
        onClose={closeDeleteModal}
        actionButton={
          <Button
            color="error"
            onClick={() => confirmDeleteAction()}
            disabled={loading}
          >
            Remove DNC Number
          </Button>
        }
        className="w-96"
      >
        <div className="text-center">
          Are you sure you want to remove {selectedDncNumber?.phoneNumber} from
          the Do Not Call list? This will allow calls to be made to this number
          again.
        </div>
      </Modal>
    </>
  );
};
